<template>
  <asc-page-loading-indicator
    :loading="isDataLoading && apiPageFailure.length === 0"
  >
    <div class="asc-flight-data-container">
      <asc-flight-data-template
        plot-editor
        :allow-add-to-issue="isAirlineUser && isIssueInvestigator"
        :tab-index="tabIndex"
        :page-plots="getPagePlots"
        :chart-data="chartData"
        :loading-plot-ids="loadingPlotIds"
        :saved-plots="getFilteredSavedPlots"
        :filter-lookups="filterLookups"
        :is-issue-investigator="isIssueInvestigator"
        :is-issue-updater="isIssueUpdater"
        :is-issue-reader="isIssueReader"
        :associating="isPlotAssociating"
        :saving="isPlotSaving"
        editable
        :is-lookup-data-loading="isLookupDataLoading"
        :hide-hidden-tabs="hideHiddenTabs"
        :deleting="isPlotDeleting"
        :on-create-page-plot="handleCreatePagePlot"
        :on-duplicate-page-plot="handleDuplicatePagePlot"
        :on-add-issue-to-page-plot="handleAddIssueToPagePlot"
        :on-close-page-plot="handleClosePagePlot"
        :on-save-page-plot="handleSavePagePlot"
        :on-add-tab-to-page-plot="handleAddTabToPagePlot"
        :on-remove-tab-from-page-plot="handleRemoveTabFromPagePlot"
        :on-update-tab-view-page-plot="handleUpdateTabViewPagePlot"
        :on-apply-page-plot="handleManualApplyPagePlot"
        :on-disable-submit="handleDisableSubmit"
        :on-open-saved-plot="handleOpenSavedPlot"
        :on-delete-saved-plot="handleDeleteSavedPlot"
        :on-tab-change="handleTabChange"
        :flight-data-analytic-comparison-permission="
          flightDataAnalyticComparisonPermission
        "
        :flight-data-flight-comparison-permission="
          flightDataFlightComparisonPermission
        "
        :flight-data-analytic-trends-permission="
          flightDataAnalyticTrendsPermission
        "
        :flight-data-flight-trends-permission="flightDataFlightTrendsPermission"
        :flight-data-acmf-trends-permission="flightDataAcmfTrendsPermission"
        :flight-data-attach-plot-to-mx-rec-permission="
          flightDataAttachPlotToMxRecPermission
        "
        :flight-data-saved-plots-permission="flightDataSavedPlotsPermission"
      ></asc-flight-data-template>

      <asc-selection-dialog
        title="Create plot type"
        subtitle="Choose the type of plot you would like to create."
        :definition="getPlotTypes"
        :show-dialog="plotTypeDialogVisible"
        :loading="isPlotTypeDialogLoading"
        :on-close="handleCreatePagePlotDialogClose"
        :on-selection="handleOnPlotTypeSelection"
      ></asc-selection-dialog>

      <asc-plot-preview-dialog
        :show-dialog="plotPreviewDialogVisible"
        :loading="isPlotPreviewLoading"
        :on-accept="handlePlotPreviewAccept"
        :on-close="handleCloseDialog"
        :plot-title="savedPlotTitle"
        :image-source="imageSource"
      ></asc-plot-preview-dialog>

      <asc-confirmation
        v-if="showAddIssuesToPlotToast"
        :definition="addIssuesToPlotToastDefinition"
        :on-close="handleAddIssuesToPlotToastClose"
      ></asc-confirmation>

      <asc-confirmation
        v-if="showDeletePlotToast"
        :definition="deletePlotToastDefinition"
        :on-close="handleDeletePlotToastClose"
        :on-toast-action="handleUndoDeletePlot"
      ></asc-confirmation>

      <asc-confirmation
        v-if="showSavePlotToast"
        :definition="savePlotToastDefinition"
        :on-close="handleSavePlotToastClose"
      ></asc-confirmation>

      <asc-add-plot-to-issue-dialog
        :show-dialog="addToIssueDialogVisible"
        :on-close="handleIssueToPagePlotClosed"
        :on-apply="handleIssueToPagePlotApplied"
        :loading="isAddToIssueDialogLoading"
        :issues="mappedMaintenanceGrid"
        :selected-issues="selectedIssues"
      ></asc-add-plot-to-issue-dialog>

      <asc-duplicate-name-dialog
        :show-dialog="dupeNameDialogVisible"
        :name="pagePlotName"
        :on-close="handleDupeNameDialogClose"
      ></asc-duplicate-name-dialog>
    </div>
  </asc-page-loading-indicator>
</template>

<script>
import { AscPlottingMixin } from '@/components/mixins'
import AscAddPlotToIssueDialog from '@/components/molecules/AscAddPlotToIssueDialog'
import AscPageLoadingIndicator from '@/components/molecules/AscPageLoadingIndicator'
import { AscFlightDataTemplate } from '@/components/templates'
import AscSelectionDialog from '@/components/molecules/AscSelectionDialog'
import AscPlotPreviewDialog from '@/components/molecules/AscPlotPreviewDialog'
import AscConfirmation from '@/components/molecules/AscConfirmation'
import AscDuplicateNameDialog from '@/components/molecules/AscDuplicateNameDialog'
import AppFeatureFlags from '@/utilities/appFeatureFlags'
import AscPlotDataMixin from './AscPlotDataMixin'
import AscPlotFiltersMixin from './AscPlotFiltersMixin'
import { PLOT_UPDATE_MESSAGES } from '@/utilities/appConsts'
import { mapState, mapGetters, mapActions, mapMutations } from 'vuex'
import { v4 as uuidv4 } from 'uuid'
import {
  GLOBAL_FILTER_CONSTS as GF,
  PLOT_CONSTS as PLC,
  USER_CONSTS as UC,
  MAINTENANCE_ISSUES_CONSTS as MIC,
} from '@/stores/consts'

export default {
  name: 'AscFlightDataContainer',

  components: {
    AscAddPlotToIssueDialog,
    AscPageLoadingIndicator,
    AscFlightDataTemplate,
    AscSelectionDialog,
    AscPlotPreviewDialog,
    AscConfirmation,
    AscDuplicateNameDialog,
  },

  mixins: [AscPlottingMixin, AscPlotDataMixin, AscPlotFiltersMixin],

  data() {
    return {
      deletePlotId: null,
      addToIssueDialogVisible: false,
      isAddToIssueDialogLoading: false,
      isAddPlotToIssueLoading: false,
      selectedIssues: [],
      appliedIssuesToPlot: [],
      showAddIssuesToPlotToast: false,
      isPlotSavedAfterAttach: false,
      addIssuesToPlotToastDefinition: [
        {
          mode: 'toast',
          type: 'success',
          text: 'Changes saved',
          position: 'bottom',
          dismissible: true,
          autoClose: true,
        },
      ],
      hideHiddenTabs: false,
    }
  },

  computed: {
    ...mapState({ apiPageFailure: 'apiPageFailure' }),
    ...mapState(GF.NAMESPACE, ['globalFilter']),
    ...mapState(PLC.NAMESPACE, [
      'plots',
      'plotImageSrc',
      'plotServerId',
      'savedPagePlots',
      'savedChartData',
    ]),
    ...mapState(UC.NAMESPACE, ['profile']),
    ...mapGetters(MIC.NAMESPACE, ['mappedMaintenanceGrid']),
    ...mapGetters(GF.NAMESPACE, [
      'airlineIdsSelected',
      'masterModelIdsSelected',
    ]),
    ...mapGetters(UC.NAMESPACE, [
      'isAcmfUser',
      'isAirlineUser',
      'isIssueInvestigator',
      'isIssueUpdater',
      'isIssueReader',
      'mappedUserAirlines',
      'flightDataAnalyticComparisonPermission',
      'flightDataFlightComparisonPermission',
      'flightDataAnalyticTrendsPermission',
      'flightDataFlightTrendsPermission',
      'flightDataAcmfTrendsPermission',
      'flightDataAttachPlotToMxRecPermission',
      'flightDataSavedPlotsPermission',
    ]),

    isFlightDataPageActive() {
      return AppFeatureFlags.getFlightDataPage()
    },

    getAirlineIds() {
      const ids =
        this.airlineIdsSelected.length > 0
          ? this.airlineIdsSelected
          : this.profile.airlines.map(a => a.id)
      return ids
    },

    isDataLoading() {
      return (
        this.isLookupDataLoading ||
        this.isPlotAssociating ||
        this.isPlotSaving ||
        this.isPlotDeleting ||
        this.isAddPlotToIssueLoading
      )
    },

    getFilteredSavedPlots() {
      return this.savedPlots
        .map(item => {
          return {
            ...item,
            globalFilterValid: this.isGlobalFilterValid(item),
          }
        })
        .filter(item => item.globalFilterValid)
    },
  },

  watch: {
    plots: {
      deep: true,
      handler(plots) {
        const pps = plots
          .filter(item => {
            let parsed = true
            try {
              JSON.parse(item.json)
            } catch (err) {
              parsed = false
            }
            return parsed
          })
          .map(item => {
            const parsed = JSON.parse(item.json)
            return {
              ...parsed,
              serverId: item.basePlotId,
              id: item.basePlotId,
              maintenanceIssueIds: item.maintenanceIssueIds,
            }
          })
        this.savedPlots = [...pps]
        this.savedPlots.forEach(item =>
          this.handleUpdateMaintenanceIssueIdsGivenNewServerId(
            item.id,
            item.maintenanceIssueIds,
          ),
        )
      },
    },
    pagePlots: {
      deep: true,
      handler(plots) {
        this[PLC.UPDATE_PAGE_PLOTS](plots)
      },
    },
    chartData: {
      deep: true,
      handler(items) {
        this[PLC.UPDATE_CHART_DATA](items)
      },
    },
    globalFilter: {
      deep: true,
      handler() {
        this.initFilterLookups()
        this.resetPagePlots()
      },
    },
  },

  created() {
    if (this.isFlightDataPageActive) {
      this.callFilterLookups()
      this.initData()
    }
  },

  mounted() {
    if (this.savedPagePlots.length > 0 || this.savedChartData.length > 0) {
      this.pagePlots = this.savedPagePlots
      this.chartData = this.savedChartData
      this.initFilterLookups()
      this.initData()
      this.resetPagePlots()
    }
  },

  methods: {
    ...mapActions(PLC.NAMESPACE, [
      PLC.GET_PLOTS,
      PLC.GET_PLOT_IMAGE_SRC,
      PLC.SAVE_PLOT,
      PLC.DELETE_PLOT,
    ]),
    ...mapActions(MIC.NAMESPACE, [
      MIC.GET_MAINTENANCE_GRID,
      MIC.UPDATE_PLOT_TO_ISSUE_ID_LINK,
      MIC.DELETE_PLOT_LINK,
      MIC.GET_MAINTENANCE_ISSUES,
    ]),
    ...mapMutations(['updateCurrentEccn']),
    ...mapMutations(PLC.NAMESPACE, [
      PLC.UPDATE_PAGE_PLOTS,
      PLC.UPDATE_CHART_DATA,
    ]),

    async initData() {
      await this[PLC.GET_PLOTS]()
    },

    handleEccnNameUpdate(eccnName) {
      this.updateCurrentEccn(eccnName)
    },

    handleCreatePagePlot() {
      this.plotTypeDialogVisible = true
    },

    handleCreatePagePlotDialogClose() {
      this.plotTypeDialogVisible = false
    },

    async handleSavePagePlot(plotId, displayToast = true) {
      const pagePlot = this.getPagePlotGivenPlotId(plotId)
      const pagePlotTitle =
        pagePlot.title.trim().length > 0 ? pagePlot.title.trim() : 'PLOT NAME'
      pagePlot.title = pagePlotTitle
      if (pagePlot) {
        const isDupe = this.isDuplicatePlotName(plotId, pagePlot.title)
        if (isDupe) {
          this.dupeNameDialogVisible = true
          this.pagePlotName = pagePlot.title
        }
        if (!isDupe) {
          const currentTabIndex = pagePlot.tabIndex
          pagePlot.tabIndex = 0
          this.isPlotSaving = true
          this.$forceUpdate()
          pagePlot.dateSaved = new Date()
          if (this.isPlotSavedAfterAttach) {
            pagePlot.options.data.tabs.map(tab => {
              tab.id = uuidv4()
              return {
                tab,
              }
            })
          }
          const imageSrc = await this.getPagePlotImageSrc(plotId)
          const savedPagePlot = {
            pagePlot,
            imageSrc,
            callGetPlots: true,
          }
          await this[PLC.SAVE_PLOT](savedPagePlot)
          this.isPlotSavedAfterAttach = false
          this.handleUpdatePagePlotGivenNewServerId(plotId, this.plotServerId)
          this.handleUpdateChartDataGivenNewServerId(plotId, this.plotServerId)
          this.showSavePlotToast = displayToast
          pagePlot.tabIndex = currentTabIndex
          setTimeout(() => {
            this.isPlotSaving = false
          }, 500)
        }
      }
    },

    async handleAddIssueToPagePlot(plotId) {
      this.selectedIssues = []
      const pagePlot = this.getPagePlotGivenPlotId(plotId)
      if (pagePlot) {
        const isDupe = this.isDuplicatePlotName(plotId, pagePlot.title)
        if (isDupe) {
          this.dupeNameDialogVisible = true
          this.pagePlotName = pagePlot.title
        }
        if (!isDupe) {
          this.addToIssueDialogVisible = true
          this.isAddToIssueDialogLoading = true
          this.isPlotSavedAfterAttach = true
          this.savedPlotId = plotId
          try {
            await this[MIC.GET_MAINTENANCE_GRID]()
            this.selectedIssues = pagePlot.maintenanceIssueIds
          } finally {
            this.isAddToIssueDialogLoading = false
          }
        }
      }
    },

    async handleIssueToPagePlotApplied(appliedIssues) {
      this.appliedIssuesToPlot = appliedIssues
      this.addToIssueDialogVisible = false

      const newIssueIds = this.appliedIssuesToPlot.filter(
        si => (this.selectedIssues || []).indexOf(si) === -1,
      )

      const deletedIds =
        this.selectedIssues?.filter(
          si => (this.appliedIssuesToPlot || []).indexOf(si) === -1,
        ) || []

      this.handleManualApplyPagePlot(
        this.savedPlotId,
        null,
        PLOT_UPDATE_MESSAGES.updatePlotMaintenanceIssueIds,
        newIssueIds,
      )

      await this.handleSavePagePlot(this.savedPlotId, false)
      this.savedPlotId = this.plotServerId
      this.isAddPlotToIssueLoading = true
      this.isPlotAssociating = true
      let operations = []

      try {
        operations = [
          ...newIssueIds.map(val =>
            this[MIC.UPDATE_PLOT_TO_ISSUE_ID_LINK]({
              plotId: this.savedPlotId,
              issueId: val,
            }),
          ),
          ...deletedIds.map(val =>
            this[MIC.DELETE_PLOT_LINK]({
              plotId: this.savedPlotId,
              issueId: val,
            }),
          ),
        ]

        if (operations.length > 0) {
          await Promise.all(operations)
          await this.initData()
        }
      } finally {
        this.appliedIssuesToPlot = []
        this.isAddPlotToIssueLoading = false
        this.isPlotAssociating = false
        this.showAddIssuesToPlotToast = true
      }
    },

    handleIssueToPagePlotClosed() {
      this.addToIssueDialogVisible = false
      this.isPlotSavedAfterAttach = false
    },

    handleAddIssuesToPlotToastClose() {
      this.showAddIssuesToPlotToast = false
    },

    async handleOpenSavedPlot(plotId, title) {
      const serverId = this.getSavedPlotServerIdGivenPlotId(plotId)
      await this[PLC.GET_PLOT_IMAGE_SRC](serverId)
      this.imageSource = this.plotImageSrc
      this.plotPreviewDialogVisible = true
      this.savedPlotTitle = title
      this.savedPlotId = plotId
    },

    handleDeleteSavedPlot(id) {
      this.isPlotDeleting = true
      this.showDeletePlotToast = true
      this.savedPlots = this.savedPlots.map(item => {
        return {
          ...item,
          isDeleting: item.id === id,
        }
      })
      this.deletePlotId = id
    },

    async handleDeletePlotToastClose() {
      this.showDeletePlotToast = false
      this.handleClosePagePlot(this.deletePlotId)
      await this[PLC.DELETE_PLOT](this.deletePlotId)
      this.isPlotDeleting = false
    },

    handleUndoDeletePlot() {
      this.savedPlots = this.savedPlots.map(item => {
        return {
          ...item,
          isDeleting: false,
        }
      })
      this.isPlotDeleting = false
      this.showDeletePlotToast = false
    },

    handleSavePlotToastClose() {
      this.showSavePlotToast = false
    },

    handleCloseDialog() {
      this.plotPreviewDialogVisible = false
    },

    handleTabChange(index) {
      this.tabIndex = index
    },
  },
}
</script>

<style lang="scss">
.asc-flight-data-container {
  background-color: $gray-100;

  & .container {
    padding: 16px;
  }
}
</style>
